import React, { useState, useEffect } from 'react';
import {
  fetchMarkdownFileContent,
  saveMarkdownFile,
  fetchImageFolders,
  fetchImagesInFolder
} from '../api';

function parseFrontmatter(content) {
  if (content.startsWith('---')) {
    const end = content.indexOf('---', 3);
    if (end !== -1) {
      const rawFront = content.slice(3, end).trim();
      const body = content.slice(end + 3).trimStart();
      const lines = rawFront.split('\n');
      const data = {};
      let currentKey = null;

      lines.forEach(line => {
        if (/^\s*- /.test(line) && currentKey) {
          const item = line.replace(/^- /, '').trim().replace(/^"|"$/g, '');
          if (!Array.isArray(data[currentKey])) data[currentKey] = [];
          data[currentKey].push(item);
        } else if (line.includes(':')) {
          const [key, ...rest] = line.split(':');
          let value = rest.join(':').trim();
          currentKey = key.trim();

          if (value === '') data[currentKey] = [];
          else if (value === 'true') data[currentKey] = true;
          else if (value === 'false') data[currentKey] = false;
          else if (value.startsWith('[')) {
            try {
              data[currentKey] = JSON.parse(value.replace(/'/g, '"'));
            } catch {
              data[currentKey] = value;
            }
          } else {
            data[currentKey] = value.replace(/^"|"$/g, '');
          }
        }
      });

      return { data, body };
    }
  }
  return { data: {}, body: content };
}

function serializeFrontmatter(data) {
  const lines = Object.entries(data).map(([key, value]) => {
    if (typeof value === 'boolean') return `${key}: ${value}`;
    if (Array.isArray(value)) return `${key}: ${JSON.stringify(value)}`;
    return `${key}: "${value}"`;
  });
  return `---\n${lines.join('\n')}\n---`;
}

const Editor = ({ fileName, onSave }) => {
  const [frontmatter, setFrontmatter] = useState({});
  const [body, setBody] = useState('');
  const [imageFolders, setImageFolders] = useState([]);
  const [imageOptions, setImageOptions] = useState({});
  const [selectedImageFolders, setSelectedImageFolders] = useState({});
  const [showSaved, setShowSaved] = useState(false);

  useEffect(() => {
    if (fileName) {
      fetchMarkdownFileContent(fileName).then(md => {
        const { data, body } = parseFrontmatter(md);
        setFrontmatter(data);
        setBody(body);
      });
      fetchImageFolders().then(setImageFolders);
    }
  }, [fileName]);

  const handleFieldChange = (key, value) => {
    setFrontmatter(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    const fm = serializeFrontmatter(frontmatter);
    const fullContent = `${fm}\n\n${body}`;
    await onSave(fileName, fullContent);
    setShowSaved(true);
  };

  const loadImages = async (folder) => {
    const images = await fetchImagesInFolder(`static/images/${folder}`);
    setImageOptions(prev => ({ ...prev, [folder]: images }));
  };

  const handleImageFolderChange = (field, folder) => {
    setSelectedImageFolders(prev => ({ ...prev, [field]: folder }));
    loadImages(folder);
  };

  const renderField = (key, value) => {
    if (key === 'date') {
      return (
        <input
          type="datetime-local"
          value={value ? new Date(value).toISOString().slice(0, 16) : ''}
          onChange={e => {
            const raw = e.target.value;
            if (!raw) return handleFieldChange(key, '');
            const iso = new Date(raw);
            if (!isNaN(iso.getTime())) {
              handleFieldChange(key, iso.toISOString());
            }
          }}
          style={styles.input}
        />
      );
    }

    if (key === 'Image') {
      const folder = selectedImageFolders[key];
      const images = folder ? imageOptions[folder] || [] : [];

      return (
        <div>
          <select
            onChange={e => handleImageFolderChange(key, e.target.value)}
            value={folder || ''}
            style={{ ...styles.input, marginBottom: 6 }}
          >
            <option value="">Select Folder</option>
            {imageFolders.map(f => <option key={f} value={f}>{f}</option>)}
          </select>
          <select
            value={value}
            onChange={e => handleFieldChange(key, e.target.value)}
            style={styles.input}
          >
            <option value="">Select Image</option>
            {images.map(img => (
              <option key={img.name} value={img.name}>
                {img.name}
              </option>
            ))}
          </select>
        </div>
      );
    }

    if (key === 'featureImage') {
      const images = imageOptions['featureImage'] || [];
      return (
        <select
          value={value}
          onClick={() => loadImages('featureImage')}
          onChange={e => handleFieldChange(key, e.target.value)}
          style={styles.input}
        >
          <option value="">Select Feature Image</option>
          {images.map(img => (
            <option key={img.name} value={img.name}>
              {img.name}
            </option>
          ))}
        </select>
      );
    }

    if (typeof value === 'boolean') {
      return (
        <input
          type="checkbox"
          checked={value}
          onChange={e => handleFieldChange(key, e.target.checked)}
          style={styles.checkbox}
        />
      );
    }

    if (Array.isArray(value)) {
      return (
        <input
          type="text"
          value={value.join(', ')}
          onChange={e =>
            handleFieldChange(key, e.target.value.split(',').map(s => s.trim()))
          }
          style={styles.input}
          placeholder="Comma-separated"
        />
      );
    }

    return (
      <input
        type="text"
        value={value}
        onChange={e => handleFieldChange(key, e.target.value)}
        style={styles.input}
      />
    );
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.header}>Editing: <span style={{ fontWeight: 'normal' }}>{fileName}</span></h2>
      <div style={styles.editorWrapper}>
        <div style={styles.frontmatterPanel}>
          <h3 style={styles.subheading}>Frontmatter</h3>
          {Object.entries(frontmatter).map(([key, value]) => (
            <div key={key} style={styles.fieldWrapper}>
              <label style={styles.label}>{key}</label>
              {renderField(key, value)}
            </div>
          ))}
        </div>

        <div style={styles.bodyPanel}>
          <h3 style={styles.subheading}>Markdown Body</h3>
          <textarea
            value={body}
            onChange={e => setBody(e.target.value)}
            style={styles.textarea}
          />
        </div>
      </div>

      <div style={styles.actions}>
        <button onClick={handleSave} style={styles.saveBtn}>💾 Save</button>
        {showSaved && (
          <div style={{ marginTop: 12, color: 'green' }}>
            Saved successfully. <button onClick={() => setShowSaved(false)}>×</button>
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    padding: '20px',
    fontFamily: 'system-ui, sans-serif',
    color: '#333',
    maxHeight: 'calc(100vh - 60px)',
    overflowY: 'auto'
  },
  header: {
    fontSize: '1.4rem',
    marginBottom: 12
  },
  editorWrapper: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '24px'
  },
  frontmatterPanel: {
    flex: '1 1 300px',
    background: '#f9f9f9',
    padding: '24px',
    borderRadius: 8,
    border: '1px solid #ddd',
    minWidth: '280px'
  },
  bodyPanel: {
    flex: '2 1 400px',
    background: '#fff',
    padding: '16px',
    borderRadius: 8,
    border: '1px solid #ddd',
    minWidth: '300px'
  },
  fieldWrapper: {
    marginBottom: 16
  },
  label: {
    display: 'block',
    fontWeight: 600,
    fontSize: '0.9rem',
    marginBottom: 4
  },
  input: {
    width: '100%',
    padding: '8px',
    fontSize: '0.95rem',
    border: '1px solid #ccc',
    borderRadius: 4
  },
  checkbox: {
    transform: 'scale(1.2)',
    marginTop: 4
  },
  textarea: {
    width: '97%',
    height: '400px',
    padding: '12px',
    fontFamily: 'monospace',
    fontSize: '0.95rem',
    border: '1px solid #ccc',
    borderRadius: 4,
    resize: 'vertical',
    overflow: 'auto'
  },
  subheading: {
    fontSize: '1rem',
    marginBottom: 12,
    borderBottom: '1px solid #ddd',
    paddingBottom: 4
  },
  actions: {
    textAlign: 'right',
    marginTop: 20
  },
  saveBtn: {
    padding: '10px 24px',
    fontSize: '1rem',
    background: '#007bff',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer'
  }
};

export default Editor;
